import { connect } from "react-redux";
import { NavLink, useRouteMatch } from "react-router-dom";
import SideSubMenu from "./SideSubMenu";
import {
  Image,
  Box,
  Flex,
  useBreakpointValue,
  List,
  ListItem,
  Divider,
} from "@chakra-ui/react";
import { useSafeSetState } from "src/common/hooks";
import { History } from "history";
import { SideMenuItemI } from "src/types/SideMenuItem";
import { SSOLinkI } from "src/types/SSOLink";
import { useLightMode } from "src/common/hooks/lightMode";
import { Accordion } from "@chakra-ui/react";
import MyAppsIcon from "src/icons/MyAppsIcon";
import ToolsIcon from "src/icons/ToolsIcon";
import { ElementType } from "react";
import LinkIcon from "src/icons/LinkIcon";
import WidgetsIcon from "src/icons/WidgetsIcon";
import SettingsIcon from "src/icons/SettingsIcon";
import { WidgetI } from "src/types/Widget";
import { WidgetLink } from "./WidgetLink";
import { ToolLink } from "./ToolLink";
import TravelerTripsIcon from "src/icons/TravelerTripsIcon";
import { HomeIcon, TicketCreditIcon } from "ctm-compass-react";
import InvoicesIcon from "src/icons/InvoicesIcon";
import LocationIcon from "src/icons/LocationIcon";
import { useTranslate } from "@tolgee/react";

type PinnedFeatureId =
  | "Elysium_Flights_Widget"
  | "ctminvoices"
  | "ut"
  | "Elysium_Traveler_Tracker_Widget";

const pinnedFeatureIds = new Set<string>([
  "Elysium_Flights_Widget",
  "ctminvoices",
  "ut",
  "Elysium_Traveler_Tracker_Widget",
]);

type PinnedFeature = {
  icon: ElementType;
  featureType: "widget" | "tool";
};

const pinnedFeatures: Record<PinnedFeatureId, PinnedFeature> = {
  Elysium_Flights_Widget: {
    icon: TravelerTripsIcon,
    featureType: "widget",
  },
  Elysium_Traveler_Tracker_Widget: {
    icon: LocationIcon,
    featureType: "tool",
  },
  ctminvoices: { icon: InvoicesIcon, featureType: "tool" },
  ut: { icon: TicketCreditIcon, featureType: "widget" },
};

/**
 * Dynamically generated menu containing My Apps, Tools and Widgets
 * along with edit option for each category.
 */

const initialState = {
  renderBrandLogo: true,
};

interface AsideMenuI {
  externalAccount?: { companyName: string; companyID: string };
  history: History;
  isCondensed: boolean;
  path: string;
  links: SideMenuItemI[];
  loading: boolean;
  obts: SSOLinkI[];
  search: string;
  ssoLinks: SSOLinkI[];
  tools: SideMenuItemI[];
  userLogo: string;
  widgets: WidgetI[];
}

const AsideMenu = ({
  externalAccount,
  history,
  isCondensed,
  path,
  links,
  loading,
  obts,
  search,
  ssoLinks,
  tools,
  userLogo,
  widgets,
}: AsideMenuI) => {
  const { t } = useTranslate();
  const [state, safeSetState] = useSafeSetState(initialState);
  const { lightMode } = useLightMode();
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const isSmall = isDesktop && isCondensed;
  const isDashboard = useRouteMatch({ path: "/", exact: true });

  const handleImageError = () => {
    safeSetState({ renderBrandLogo: false });
  };

  const renderHomeIcon = path !== "/";
  const params = new URLSearchParams(search);
  const paramId = params.get("id");

  const favorites = [
    ...tools.filter((tool) => pinnedFeatureIds.has(tool.identifier)),
    ...widgets.filter(
      (widget) =>
        pinnedFeatureIds.has(widget.identifier) &&
        widget.properties.isVisible &&
        pinnedFeatures[widget.identifier].featureType === "widget",
    ),
  ];

  const filteredTools = tools.filter(
    (tool) => !pinnedFeatureIds.has(tool.identifier),
  );

  const filteredWidgets = widgets.filter(
    (widget) =>
      !pinnedFeatureIds.has(widget.identifier) && widget.properties.isVisible,
  );

  if (loading) {
    return <></>;
  }

  return (
    <Box
      as="aside"
      pt={10}
      color={lightMode ? "var(--light-textColor)" : "white"}
    >
      {state.renderBrandLogo && userLogo && (
        <Flex
          justify="center"
          w={isCondensed ? "0" : "var(--asideExpandedWidth)"}
          overflow="hidden"
          transition="width 0.5s ease"
          px={isCondensed ? 0 : 4}
        >
          <Image
            src={userLogo}
            onError={handleImageError}
            aria-hidden
            maxH="100px"
            objectFit="contain"
          />
        </Flex>
      )}
      <Box as="nav" px={"16px"} paddingTop="16px" className="aside-nav">
        <Accordion allowMultiple>
          <List>
            {renderHomeIcon && (
              <ListItem
                borderRadius={"4px"}
                marginY={isSmall ? "4px" : "8px"}
                width="100%"
              >
                <NavLink
                  exact
                  to="/"
                  data-aut="Aside|Home"
                  aria-label={t({
                    key: "dashboard.sideMenu.dashboard",
                    defaultValue: "Dashboard",
                  })}
                  className={`asidemenu-link${isCondensed ? " small" : ""}`}
                >
                  <Flex
                    title={
                      isCondensed
                        ? t({
                            key: "dashboard.sideMenu.dashboard",
                            defaultValue: "Dashboard",
                          })
                        : undefined
                    }
                  >
                    <HomeIcon
                      fontSize="22px"
                      color={
                        lightMode ? "var(--chakra-colors-gray-500)" : "#C7C7C7"
                      }
                    />
                    {isCondensed ? null : (
                      <Box
                        as="span"
                        alignSelf="center"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        ml="12px"
                      >
                        {t({
                          key: "dashboard.sideMenu.dashboard",
                          defaultValue: "Dashboard",
                        })}
                      </Box>
                    )}
                  </Flex>
                </NavLink>
              </ListItem>
            )}
            {favorites?.length
              ? favorites.map((item: any) => {
                  const pinnedFeature: PinnedFeature =
                    pinnedFeatures[item.identifier];

                  return (
                    <ListItem
                      key={item.id}
                      borderRadius={"4px"}
                      marginY={isSmall ? "4px" : "8px"}
                      width="100%"
                    >
                      {pinnedFeature.featureType === "widget" ? (
                        !isDashboard ? (
                          <ToolLink
                            href={{
                              pathname: "/",
                              search: `?widgetId=widget${item.id}`,
                            }}
                            isSmall={isSmall}
                            item={item}
                            LeftIcon={pinnedFeature.icon}
                            path={path}
                            paramId={paramId}
                          />
                        ) : (
                          <WidgetLink
                            item={item}
                            LeftIcon={pinnedFeature.icon}
                            isSmall={isSmall}
                          />
                        )
                      ) : (
                        <ToolLink
                          href={`${item.href}?id=${item.id}`}
                          isSmall={isSmall}
                          item={item}
                          LeftIcon={pinnedFeature.icon}
                          path={path}
                          paramId={paramId}
                        />
                      )}
                    </ListItem>
                  );
                })
              : null}
            <Divider />
            {ssoLinks.length || obts.length ? (
              <SideSubMenu
                history={history}
                label={t({
                  key: "dashboard.sideMenu.myApps",
                  defaultValue: "My Apps",
                })}
                isCondensed={isCondensed}
                isSmall={isSmall}
                isSSO={true}
                items={[...obts, ...ssoLinks]}
                icon={MyAppsIcon}
              />
            ) : null}
            {filteredWidgets?.length ? (
              <SideSubMenu
                label={t({
                  key: "dashboard.sideMenu.widgets",
                  defaultValue: "Widgets",
                })}
                isCondensed={isCondensed}
                isSmall={isSmall}
                items={filteredWidgets}
                isWidget={true}
                icon={WidgetsIcon}
              />
            ) : null}
            {filteredTools?.length ? (
              <SideSubMenu
                label={t({
                  key: "dashboard.sideMenu.tools",
                  defaultValue: "Tools",
                })}
                isCondensed={isCondensed}
                isSmall={isSmall}
                items={filteredTools}
                path={path}
                paramId={paramId}
                icon={ToolsIcon}
              />
            ) : null}
            {links?.length ? (
              <SideSubMenu
                label={t({
                  key: "dashboard.sideMenu.links",
                  defaultValue: "Links",
                })}
                isCondensed={isCondensed}
                isSmall={isSmall}
                items={links}
                icon={LinkIcon}
              />
            ) : null}

            {widgets?.length && !externalAccount ? (
              <SideSubMenu
                label={t({
                  key: "dashboard.sideMenu.settings",
                  defaultValue: "Settings",
                })}
                isCondensed={isCondensed}
                isSmall={isSmall}
                items={widgets}
                isWidget={true}
                widgetEditMode={true}
                icon={SettingsIcon}
              />
            ) : null}
          </List>
          <Divider />
        </Accordion>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  externalAccount: state.externalAccount?.account,
  loading: state.user.loading,
  links: state.links,
  obts: state.obts,
  ssoLinks: state.ssoLinks,
  tools: state.tools,
  widgets: state.widgets,
});

export default connect(mapStateToProps)(AsideMenu);
